import React from 'react';
import { styled } from '@mui/material/styles';
import generateNonce from 'utils/generateNonce';
import BulkEditableProductList from './_bulkEditableProductList';
import LinearProgress from '@mui/material/LinearProgress';
import {
  GetProductListItems,
  CreateOrUpdateProductListItem,
} from 'services/productList';
import { Box, Pagination } from '@mui/material';
import SampleCsvLink from './_sampleCsvLink';
import { ProductListItemDto } from 'models/dtoTypes';
import { ProductListItemAttributes } from 'kosmos/models';
import ProductCsvUploadDialog from '../ProductCsvUploadDialog';
import CreateResourceModal, { ISchema } from 'kosmos/dialogs/CreateResourceModal';
import { useTranslation } from 'react-i18next';
import { certifications } from './certification';

const PREFIX = 'ProductListEditTable';

const classes = {
  root: `${PREFIX}-root`,
  actions: `${PREFIX}-actions`,
  pagination: `${PREFIX}-pagination`,
};

const Root = styled('div')(() => ({
  position: 'relative',
  width: '100%',
  [`& .${classes.pagination}`]: {
    paddingTop: 10,
    paddingBottom: 10,
  },
}));

type ProductListItemsWithCount = {
  pageCount?: number;
  count?: number;
  page?: number;
  items?: ProductListItemDto[] | null;
};

interface ICountryOption {
  label: string;
  alpha3: string;
}

interface IProps {
  countries: ICountryOption[];
  productListId: string;
  onErrorResponse?: (e: unknown) => void;
}

export default function ProductListEditTable(props: IProps) {
  const { productListId, countries, onErrorResponse } = props;
  const { t } = useTranslation('default');
  const [openAddNewItem, setOpenAddNewItem] = React.useState(false);
  const [openCsvUploader, setOpenCsvUploader] = React.useState(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [page, setPage] = React.useState(1);
  const [nonce, setNonce] = React.useState('');
  const [result, setResult] = React.useState<ProductListItemsWithCount>();

  const rows = result?.items || [];

  const loadItems = async (pageIndex: number) => {
    setLoading(true);
    const list = await GetProductListItems(productListId, { pageIndex });
    setResult(list);
    setLoading(false);
  };

  React.useEffect(() => {
    void loadItems(page);
  }, [nonce, page]);

  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleCheckCanEdit = () => {
    return false;
  };

  const handleFinish = () => {
    setOpenCsvUploader(false);
    setNonce(generateNonce());
  };

  const generateCreateSchema = (): ISchema => ({
    fields: [
      {
        label: t('Company Name'),
        name: 'companyName',
        required: false,
        widget: 'text',
        description: t('the-official-name-for-this-supplier'),
      },
      {
        label: t('country'),
        name: 'countryCode',
        required: false,
        widget: 'select',
        options: countries.map((o) => ({
          alias: o.alpha3 || '',
          label: o.label,
        })),
        description: t('country'),
      },
      {
        label: t('Certificates'),
        name: 'certificates',
        required: false,
        widget: 'multipleSelect',
        description: t('Certificates'),
        optionAliases: certifications,
      },
      {
        label: t('Product Description'),
        name: 'productDescription',
        required: true,
        widget: 'text',
        description: t('Product description'),
      },
      {
        label: t('Transaction ID'),
        name: 'transactionId',
        required: false,
        widget: 'text',
        description: t('Transaction ID'),
      },
    ],
  });


  const handleCreateOrUpdateItem = async (row: ProductListItemAttributes) => {
    if (!productListId) {
      return;
    }
    try {
      const res = await CreateOrUpdateProductListItem(productListId, row);
      return res;
    } catch (e) {
      onErrorResponse?.(e);
    }
  };

  const handleAddNew = async (data: ProductListItemAttributes) => {
    await handleCreateOrUpdateItem(data);
    setOpenAddNewItem(false);
    setNonce(generateNonce());
  };

  return (
    <Root>
      {loading && <LinearProgress />}
      {!loading && (
        <BulkEditableProductList<ProductListItemAttributes>
          countries={countries}
          list={rows}
          onCanEdit={handleCheckCanEdit}
          onAdd={() => setOpenAddNewItem(true)}
          onCsvUpload={() => setOpenCsvUploader(true)}
          onDelete={undefined}
          onSubmit={undefined}
        />
      )}
      {result && (
        <div className={classes.pagination}>
          <Pagination count={result.pageCount} page={page} onChange={handleChangePage} />
        </div>
      )}
      {productListId && (
        <>
          <ProductCsvUploadDialog
            productListId={productListId || ''}
            visible={openCsvUploader}
            onClose={() => setOpenCsvUploader(false)}
            onFinish={handleFinish}
          />
          {!!countries.length && (
            <CreateResourceModal<ProductListItemAttributes>
              label={t('Add Product')}
              open={openAddNewItem}
              onClose={() => setOpenAddNewItem(false)}
              onSave={handleAddNew}
              schema={generateCreateSchema()}
            />
          )}
        </>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
        <Box sx={{ flex: '1 1 auto' }} />
        <SampleCsvLink />
      </Box>
    </Root>
  );
}
